import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ExternalLink,
  CheckCircle,
  Shield,
  Smartphone,
  Palette,
  Bell,
  TrendingUp,
  Zap,
  Eye,
  Brain,
  Search,
  Star,
  BookOpen,
  ShoppingCart,
} from "lucide-react";
import { siteConfig } from "@/config";
import PageTransition from "@/components/PageTransition";
import AnimatedText from "@/components/AnimatedText";
import { cn } from "@/lib/utils";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import { smoothScrollTo } from "@/lib/smoothScroll";
import { motion } from "framer-motion";
import Tilt from "react-parallax-tilt";
import { BsArrowLeft } from "react-icons/bs";
import { RxExternalLink } from "react-icons/rx";
import { FiGithub } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  getProjectDetails,
  hasProjectFeatures,
  hasProjectPrivacyPolicy,
} from "@/lib/project-details-readme";

const ProjectDetail = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  // Get project details from the dynamic content file
  const projectDetails = projectId ? getProjectDetails(projectId) : {};

  // Icon mapping for dynamic icons
  const iconMap: Record<string, any> = {
    Eye,
    Smartphone,
    CheckCircle,
    TrendingUp,
    Bell,
    Palette,
    Shield,
    Zap,
    Brain,
    Search,
    Star,
    BookOpen,
    ShoppingCart,
  };

  // Color mapping for dynamic colors
  const colorMap: Record<string, string> = {
    blue: "bg-blue-500/20 text-blue-400",
    green: "bg-green-500/20 text-green-400",
    purple: "bg-purple-500/20 text-purple-400",
    orange: "bg-orange-500/20 text-orange-400",
    red: "bg-red-500/20 text-red-400",
    indigo: "bg-indigo-500/20 text-indigo-400",
    emerald: "bg-emerald-500/20 text-emerald-400",
    pink: "bg-pink-500/20 text-pink-400",
    yellow: "bg-yellow-500/20 text-yellow-400",
  };

  useEffect(() => {
    // Find the project in the config immediately
    const foundProject = siteConfig.projects.items.find(
      (p) => p.id === projectId
    );

    if (!foundProject) {
      navigate("/not-found");
      return;
    }

    // Set project immediately to avoid loading screen
    setProject(foundProject);
    setLoading(false);

    // Handle visibility with a short delay for animations
    const visibilityTimer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    // Set document title
    document.title = `${foundProject.title} | Project Details`;

    // Add structured data for this project
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: foundProject.title,
      description: foundProject.description,
      mainEntity: {
        "@type": "CreativeWork",
        name: foundProject.title,
        description: foundProject.description,
        image: foundProject.image,
        url: window.location.href,
      },
    };

    // Add the structured data script
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      clearTimeout(visibilityTimer);
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [projectId, navigate]);

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/");

    // Smooth scroll to projects section after a short delay to allow page transition
    setTimeout(() => {
      smoothScrollTo("projects");
    }, 100);
  };

  // Format date string
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  // With our improved transitions, we don't need a loading state anymore
  // Just render the content immediately but control its visibility with animations
  return (
    <PageTransition>
      <div className="bg-black text-white min-h-screen noise-bg">
        <header>
          <Navbar />
        </header>

        <main
          id="main-content"
          className="container mx-auto px-4 py-20 md:py-32"
        >
          {/* Back button with animation */}
          <Link
            to="/#projects"
            className={cn(
              "inline-flex items-center gap-2 mb-12 group transition-all duration-500 transform",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-4"
            )}
            onClick={handleBackClick}
          >
            <ChevronLeft className="group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="uppercase text-sm tracking-widest relative overflow-hidden group-hover:after:scale-x-100 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:-bottom-1 after:left-0 after:bg-white after:origin-bottom-right after:transition-transform after:duration-300">
              Back to Projects
            </span>
          </Link>

          {project && (
            <>
              {/* Project header */}
              <div
                className={cn(
                  "mb-16 transition-all duration-700",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                )}
              >
                <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-4">
                  <AnimatedText text={project.title} delay={100} />
                </h1>

                <div className="flex flex-wrap gap-3 mt-6">
                  {project.tags.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className={cn(
                        "text-xs uppercase tracking-wider bg-black px-3 py-1 transition-all duration-300",
                        isVisible
                          ? "transform translate-y-0 opacity-100"
                          : "transform -translate-y-2 opacity-0"
                      )}
                      style={{ transitionDelay: `${index * 50 + 200}ms` }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Project image with creative reveal animation */}
              <div
                className={cn(
                  "w-full aspect-video mb-16 border border-white/50 overflow-hidden relative group",
                  isVisible ? "opacity-100" : "opacity-0",
                  "transition-all duration-1000"
                )}
                style={{ transitionDelay: "200ms" }}
              >
                {/* Animated reveal mask */}
                <div className="absolute inset-0 z-10">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      className="absolute h-full bg-black"
                      style={{
                        left: `${i * 10}%`,
                        width: "10%",
                        transform: isVisible
                          ? "translateY(100%)"
                          : "translateY(0)",
                        transition: `transform 600ms cubic-bezier(0.65, 0, 0.35, 1) ${200 + i * 50
                          }ms`,
                      }}
                    />
                  ))}
                </div>

                {/* Image overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-[1]"></div>

                {/* Image with smooth loading transition */}
                <div
                  className={cn(
                    "w-full h-full transition-transform duration-[1.5s] ease-out",
                    imageLoaded ? "scale-100" : "scale-110"
                  )}
                >
                  <img
                    src={project.image}
                    alt={project.title}
                    className={cn(
                      "w-full h-full object-cover transition-all duration-[1.5s]",
                      "group-hover:scale-110",
                      imageLoaded
                        ? "opacity-100 blur-none"
                        : "opacity-0 blur-sm"
                    )}
                    onLoad={() => setImageLoaded(true)}
                  />
                </div>
              </div>

              {/* Project details with staggered reveal */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                {/* Main content area */}
                <div
                  className={cn(
                    "md:col-span-2 transition-all duration-700",
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-10"
                  )}
                  style={{ transitionDelay: "300ms" }}
                >
                  <h2 className="text-2xl font-display mb-6">
                    PROJECT OVERVIEW
                  </h2>
                  <div className="space-y-6">
                    {project.fullDescription
                      .split("\n\n")
                      .map((paragraph: string, index: number) => {
                        // Check if this paragraph contains key features
                        if (paragraph.includes("**Key Features:**")) {
                          return null; // Skip the "Key Features:" header as we'll handle it separately
                        }

                        // Check if this is a feature list item
                        if (paragraph.startsWith("- **")) {
                          return null; // Skip individual feature items as we'll handle them separately
                        }

                        return (
                          <p
                            key={index}
                            className="opacity-0 animate-fade-in text-lg leading-relaxed"
                            style={{
                              animationDelay: `${500 + index * 150}ms`,
                              animationFillMode: "forwards",
                            }}
                          >
                            {paragraph}
                          </p>
                        );
                      })}
                  </div>

                  {/* Dynamic Key Features Section */}
                  {hasProjectFeatures(projectId!) && (
                    <div
                      className="mt-12 opacity-0 animate-fade-in"
                      style={{
                        animationDelay: "800ms",
                        animationFillMode: "forwards",
                      }}
                    >
                      <h3 className="text-2xl font-display mb-8 flex items-center gap-3 tracking-wide">
                        <Zap className="w-8 h-8 text-yellow-400" />
                        Key Features
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {projectDetails.features?.map((feature, index) => {
                          const IconComponent = iconMap[feature.icon];
                          const colorClass = colorMap[feature.color];

                          return (
                            <div
                              key={index}
                              className="flex items-start gap-4 p-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all duration-300"
                            >
                              <div className={`p-2 rounded-lg ${colorClass}`}>
                                {IconComponent && (
                                  <IconComponent className="w-6 h-6" />
                                )}
                              </div>
                              <div>
                                <h4 className="font-medium text-lg mb-2 tracking-wide">
                                  {feature.title}
                                </h4>
                                <p className="text-gray-300">
                                  {feature.description}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Dynamic Privacy Policy Section */}
                  {hasProjectPrivacyPolicy(projectId!) && (
                    <div
                      className="mt-16 opacity-0 animate-fade-in"
                      style={{
                        animationDelay: "1000ms",
                        animationFillMode: "forwards",
                      }}
                    >
                      <div className="bg-gradient-to-br from-black/90 via-gray-900/90 to-black/90 border border-white/20 rounded-xl overflow-hidden backdrop-blur-sm">
                        {/* Header with gradient accent */}
                        <div className="bg-gradient-to-r from-blue-600/20 via-purple-600/10 to-transparent p-8 border-b border-white/10">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-blue-500/30 to-purple-500/20 rounded-xl border border-white/10">
                              <Shield className="w-8 h-8 text-blue-400" />
                            </div>
                            <div>
                              <h2 className="text-3xl font-display tracking-wide text-white">
                                Privacy Policy
                              </h2>
                              <p className="text-gray-400 text-sm mt-1">
                                Your privacy is our priority
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Content area */}
                        <div className="p-8 prose prose-invert max-w-none">
                          <ReactMarkdown
                            className="text-white space-y-4"
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: ({ children }) => (
                                <h1 className="text-3xl font-display mb-6 text-white tracking-wide border-b border-white/10 pb-4">
                                  {children}
                                </h1>
                              ),
                              h2: ({ children }) => (
                                <div className="flex items-center gap-3 mt-10 mb-4">
                                  <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
                                  <h2 className="text-xl font-semibold text-white tracking-wide">
                                    {children}
                                  </h2>
                                </div>
                              ),
                              h3: ({ children }) => (
                                <h3 className="text-lg font-medium mb-3 mt-6 text-blue-300 tracking-wide">
                                  {children}
                                </h3>
                              ),
                              p: ({ children }) => (
                                <p className="text-gray-300 leading-relaxed mb-4">
                                  {children}
                                </p>
                              ),
                              ul: ({ children }) => (
                                <ul className="space-y-3 text-gray-300 mb-6 ml-1">
                                  {children}
                                </ul>
                              ),
                              li: ({ children }) => (
                                <li className="flex items-start gap-3 text-gray-300">
                                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                                  <span>{children}</span>
                                </li>
                              ),
                              strong: ({ children }) => (
                                <strong className="text-white font-semibold">
                                  {children}
                                </strong>
                              ),
                              em: ({ children }) => (
                                <em className="text-gray-400 not-italic text-sm">
                                  {children}
                                </em>
                              ),
                              a: ({ children, href }) => (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/50 underline-offset-2 transition-colors"
                                >
                                  {children}
                                </a>
                              ),
                              table: ({ children }) => (
                                <div className="overflow-x-auto my-6 border border-white/10 rounded-xl">
                                  <table className="w-full text-sm">
                                    {children}
                                  </table>
                                </div>
                              ),
                              thead: ({ children }) => (
                                <thead className="bg-white/5 border-b border-white/10">
                                  {children}
                                </thead>
                              ),
                              th: ({ children }) => (
                                <th className="text-left py-4 px-5 text-white font-semibold text-sm uppercase tracking-wider">
                                  {children}
                                </th>
                              ),
                              tbody: ({ children }) => (
                                <tbody className="divide-y divide-white/5">
                                  {children}
                                </tbody>
                              ),
                              tr: ({ children }) => (
                                <tr className="hover:bg-white/5 transition-colors">
                                  {children}
                                </tr>
                              ),
                              td: ({ children }) => (
                                <td className="py-4 px-5 text-gray-300">
                                  {children}
                                </td>
                              ),
                              blockquote: ({ children }) => (
                                <blockquote className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-500/5 rounded-r-lg text-gray-300 mb-4">
                                  {children}
                                </blockquote>
                              ),
                            }}
                          >
                            {projectDetails.privacyPolicy}
                          </ReactMarkdown>
                        </div>

                        {/* Footer */}
                        <div className="px-8 py-6 bg-white/5 border-t border-white/10 flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <Shield className="w-4 h-4" />
                            <span>Your data is protected</span>
                          </div>
                          <a
                            href="mailto:gauravgadhari39@gmail.com"
                            className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-2 transition-colors"
                          >
                            Contact Support
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sidebar with project details */}
                <div
                  className={cn(
                    "transition-all duration-700",
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-10"
                  )}
                  style={{ transitionDelay: "400ms" }}
                >
                  <div className="border border-white/50 p-6 space-y-8 relative overflow-hidden group backdrop-blur-sm bg-black/30">
                    {/* Animated corner highlight on hover */}
                    <div className="absolute -top-[150%] -left-[150%] w-[300%] h-[300%] bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 rotate-45 transition-opacity duration-700"></div>

                    <div>
                      <h3 className="text-sm uppercase tracking-wider mb-3">
                        Year
                      </h3>
                      <p className="text-xl">{project.yearCreated}</p>
                    </div>

                    {project.status && (
                      <div>
                        <h3 className="text-sm uppercase tracking-wider mb-3">
                          Status
                        </h3>
                        <p className="text-xl">
                          <span
                            className={cn(
                              "inline-block w-2 h-2 rounded-full mr-2",
                              project.status === "Under Development"
                                ? "bg-yellow-400"
                                : project.status === "Under Testing"
                                  ? "bg-green-400"
                                  : "bg-blue-400"
                            )}
                          ></span>
                          {project.status}
                        </p>
                      </div>
                    )}

                    {project.launchDate && (
                      <div>
                        <h3 className="text-sm uppercase tracking-wider mb-3">
                          Estimated Launch
                        </h3>
                        <p className="text-xl">
                          {formatDate(project.launchDate)}
                        </p>
                      </div>
                    )}

                    <div>
                      <h3 className="text-sm uppercase tracking-wider mb-3">
                        Technologies
                      </h3>
                      <ul className="space-y-2">
                        {project.technologies.map(
                          (tech: string, index: number) => (
                            <li
                              key={index}
                              className="opacity-0 animate-fade-in flex items-center"
                              style={{
                                animationDelay: `${600 + index * 80}ms`,
                                animationFillMode: "forwards",
                              }}
                            >
                              <span className="w-1 h-1 bg-white inline-block mr-2"></span>
                              {tech}
                            </li>
                          )
                        )}
                      </ul>
                    </div>

                    {project.link && project.link !== "#" && (
                      <div className="pt-4">
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 border border-white/80 px-6 py-3 uppercase text-sm tracking-widest transition-all duration-300 relative group overflow-hidden"
                        >
                          <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                            View Live Project
                          </span>
                          <ExternalLink
                            size={16}
                            className="relative z-10 transition-colors duration-300 group-hover:text-black"
                          />
                          <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Call to action */}
              <div
                className={cn(
                  "mt-24 text-center transition-all duration-700",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                )}
                style={{ transitionDelay: "700ms" }}
              >
                <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-8">
                  <AnimatedText
                    text="INTERESTED IN WORKING TOGETHER?"
                    delay={800}
                  />
                </h2>

                <Link
                  to="/#contact"
                  className="inline-block border border-white/80 px-8 py-4 uppercase text-sm tracking-widest transition-all duration-300 relative group overflow-hidden"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/");

                    // Smooth scroll to contact section after a short delay
                    setTimeout(() => {
                      smoothScrollTo("contact");
                    }, 100);
                  }}
                >
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                    Get in Touch
                  </span>
                  <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                </Link>
              </div>
            </>
          )}
        </main>

        <footer>
          <Footer />
        </footer>
      </div>
    </PageTransition>
  );
};

export default ProjectDetail;
